import { useState, useEffect } from "react";
import RoleNavbar from "./RoleNavbar";
import Spinner from "../Spinner";
import { useApi } from "../../api/ApiProvider";
import Api from "../../axiosApi/api";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import intl from "components/i18n/ReactIntlWrapper";
import { USER_STATUS } from "../../common/constants";
import { SecurityRole } from "axiosApi/models";
import { handleAPIError } from "common/errorHandler";
import ErrorSummary from "components/common/ErrorSummary";
import { ActivateSecurityRoleCommand } from "axiosApi/models";
import ModalUsers from "./Users/ModalUsers";
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Grid,
  Switch,
  Link as MuiLink
} from "@mui/material";
import { useDialog } from 'common/DialogProvider';
import { getUserEntity } from 'common/UserEntityProvider';
import { DEFAULT_PAGINATION } from 'hooks/useSearch';
import { DataGrid, GridColDef, GridInitialState, GridSortModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { usePaginationPreference } from 'hooks/usePaginationPreference';


export type RolesInput = {
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
  idStatus: number;
  entityId: number;
  entityTypeId: number;
  userEntityId: number;
  idScope: number | null;
};

export type SearchResult = {
  totalRecords: number | null;
  queryResult: SecurityRole[];
};

const Roles = () => {
  const [input, setInput] = useState<RolesInput | null>({
    queryTerm: "",
    currentPage: 1,
    sortValue: "Name",
    orderValue: "asc",
    idStatus: null,
    entityId: null,
    entityTypeId: null,
    userEntityId: null,
    idScope: null,
  });

  const [searchResult, setSearchResult] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: [],
  });

  const api: Api = useApi();

  const [pageCount, setpageCount] = useState<number>(0);

  const [limit, setLimit] = useState<number>(() => {
    return parseInt(localStorage.getItem("itemsPerPageRoles"), 10) || 10;
  });

  useEffect(() => {
    setLimit(parseInt(localStorage.getItem("itemsPerPageRoles"), 10) || 10);
  }, []);

  const toast = useToastMessageQueue();

  const [scopes, setScopes] = useState(null);

  const [errors, setErrors] = useState(null);

  const [entities, setEntities] = useState(null);

  const [loading, setLoading] = useState<boolean>(false);

  const [pagination, setPagination] = usePaginationPreference('roles', 10);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "Name", sort: "asc" }]);
  const { openDialog, closeDialog } = useDialog();
  const [isAssignedUsers, setIsAssignedUsers] = useState(null);
  const entityType = getUserEntity();
  const navigate = useNavigate();

  // Handle pagination changes
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    setPagination(model);
  };

  // Handle sort changes
  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const fetchSecurityRoles = async (
    term?: string,
    pageNum?: number,
    pageSize?: number,
    sort?: string,
    order?: string,
    f?: Array<string>
  ) => {
    setLoading(true);
    if (input.idScope) {
      f = f || [];
      f.push(`scopes:${input.idScope}`);
    }
    if (input.entityId && input.entityTypeId) {
      f = f || [];
      f.push(`entity:${input.entityId}`);
      f.push(`entitytype:${input.entityTypeId}`);
    }
    if (input.idStatus !== null) {
      f = f || [];
      f.push(`active:${input.idStatus}`);
    }
    const response = await api.employeeApi
      .apiVversionSecurityRoleSearchGet(
        "1",
        input?.queryTerm || term,
        (pagination?.page || pageNum || 0) + 1, // Convert 0-based to 1-based for API
        pagination?.pageSize || pageSize || 10,
        sortModel[0]?.field || sort || "Name",
        sortModel[0]?.sort || order || "asc",
        f,
        {}
      )
      .then((response) => {
        if (response.data.data === null) {
          setSearchResult({
            totalRecords: null,
            queryResult: null,
          });
        } else {
          setSearchResult({
            totalRecords: response.data?.data?.totalRecords,
            queryResult: response.data?.data?.queryResult,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  const fetchScopes = async () => {
    const response = await api.employeeApi
      .apiVversionScopeAllGet("1", input.currentPage, 200, {})
      .then((response) => {
        if (response.data.data === null) {
          setScopes({
            totalRecords: null,
            queryResult: null,
          });
        } else {
          setScopes({
            totalRecords: response.data?.data?.totalRecords,
            queryResult: response.data?.data?.queryResult,
          });
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  const fetchEntities = async () => {
    setLoading(true);
    const response = await api.employeeApi
      .apiVversionListEntitiesGet("1", {})
      .then((response) => {
        if (response.data.data === null) {
          setEntities({
            totalRecords: null,
            queryResult: null,
          });
        } else {
          setEntities({
            totalRecords: response.data?.data?.totalRecords,
            queryResult: response.data?.data?.queryResult,
          });
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSecurityRoles();
    fetchScopes();
    //TODO CC: unexistant endpoint?
    //fetchUsers();
    fetchEntities();
  }, [input, limit]);

  // Add useEffect to handle pagination and sorting changes
  useEffect(() => {
    fetchSecurityRoles();
  }, [pagination, sortModel]);

  const handlePageClick = (pageNum) =>
    setInput({ ...input, currentPage: pageNum });

  const nextPage = () =>
    setInput({ ...input, currentPage: input.currentPage + 1 });

  const prevPage = () =>
    setInput({ ...input, currentPage: input.currentPage - 1 });

  const initialPage = () => setInput({ ...input, currentPage: 1 });

  const finalPage = () => setInput({ ...input, currentPage: pageCount });

  const onSearch = (queryString, idScope, entityId, entityTypeId, idStatus) => {
    setInput({
      ...input,
      queryTerm: queryString,
      idScope: idScope,
      entityId: entityId,
      entityTypeId: entityTypeId,
      idStatus: idStatus,
    });
  };

  const INITIAL_STATE: GridInitialState = {
		pagination: {
		  paginationModel: {
			page: DEFAULT_PAGINATION.pageNum,
			pageSize: DEFAULT_PAGINATION.pageSize,
		  },
		},
		sorting: {
		  sortModel: [
			{
			  field: 'Name',
			  sort: 'asc',
			},
		  ],
		},
	};

  const handleDelete = (id) => {
      const dialogId = openDialog({
        title: intl.get("delete.modal.title"),
        message: intl.get("delete.modal.roleRow.message"),
        type: 'confirm',
        confirmButton: {
          confirmLabel: intl.get("delete.modal.delete.button"),
          handleConfirm: async () => {
            const response = api.employeeApi
                .apiVversionSecurityRoleIdDelete(id, "1", {})
                .then((response) => {
                  let newSecurityRoles = searchResult.queryResult.filter(
                    (role) => role.id !== id
                  );
                  setSearchResult({
                    ...searchResult,
                    queryResult: newSecurityRoles,
                  });
                  toast.success({
                    message: intl.get("roleRow.toast.success.handleDelete"),
                  });
                  closeDialog(dialogId);
                })
                .catch((error) => {
                    handleAPIError(error, toast, errors);
                    closeDialog(dialogId);
                });
          }
        },
        cancelButton: {
          cancelLabel: intl.get("delete.modal.cancel.button"),
          handleCancel: () => {
            closeDialog(dialogId);
          },
        }
      });
    };

    const handleStatus = async (id, newStatus) => {
      setLoading(true);
      let cmd: ActivateSecurityRoleCommand = {
        active: newStatus, // Use the new status directly, don't invert it
        id: id,
      };
      
      try {
        const response = await api.employeeApi
          .apiVversionSecurityRoleActivatePut("1", cmd, {});
        
        if (response.data.data) {
          // Update the local state with the new data from server
          const updateQueryResult = searchResult.queryResult.map((role) => {
            if (role.id === response.data.data.id) {
              return response.data.data;
            } else {
              return role;
            }
          });
          setSearchResult({ ...searchResult, queryResult: updateQueryResult });
          toast.success({
            message: intl.get("roleRow.toast.success.handleStatus"),
          });
        } else {
          // If no data returned, refetch the entire list to ensure consistency
          await fetchSecurityRoles();
        }
      } catch (error) {
        handleAPIError(error, toast, errors);
        // Refetch data on error to ensure UI is in sync
        await fetchSecurityRoles();
      } finally {
        setLoading(false);
      }
    };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: intl.get('roleList.table.thead.name'),
      width: 250,
      flex: 1
    },
	  {
      field: 'description',
      headerName: intl.get('roleList.table.thead.description'),
      width: 250,
      flex: 1
    },
    {
      field: 'users',
      headerName: intl.get('roleList.table.thead.users'),
      width: 250,
      flex: 1,
      renderCell: (params) => (
        <>
          <MuiLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setIsAssignedUsers(params.row.id);
            }}
            underline="hover"
          >
            {params.row.users.length}{" "}
            {params.row.users.length > 1
              ? intl.get("roleRow.users")
              : intl.get("roleRow.user")}
          </MuiLink>
          <ModalUsers
            isOpen={isAssignedUsers === params.row.id}
            closeModal={() => setIsAssignedUsers(null)}
            users={params.row.users}
          />
        </>
      ),
    },
    {
      field: 'status',
      headerName: intl.get('roleList.table.thead.status'),
      width: 250,
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <Switch
            checked={params.row.active}
            onChange={(event, checked) => handleStatus(params.row.id, checked)}
            slotProps={{
              input: { 'aria-label': 'Status Switch' }
            }}
          />
          <Typography variant="body2">
            {params.row.active
              ? intl.get("roleRow.active")
              : intl.get("roleRow.inactive")}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onEdit={() => navigate(`/role/detail/${params.row.id}`)}
            onDelete={() => handleDelete(params.row.id)}
            identifier="ui.roles.list.row-actions"
          />
      ),
    },
  ];

  return (
    <Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
        {intl.get("roles.header")}
      </Typography>
      <Box>
        <ErrorSummary
          errors={errors}
          showEverything={false}
          mode="warning"
        />
        <RoleNavbar
          entities={entities?.queryResult}
          status={USER_STATUS}
          scopes={scopes?.queryResult}
          onSearch={onSearch}
          input={input}
        />
        <Box sx={{ width: '100%', display: 'flex' }} mt={4}>
          {loading && searchResult && <Spinner />}
          {searchResult && (
            <DataGrid
              rows={searchResult?.queryResult || []}
              columns={columns}
              rowCount={searchResult?.totalRecords || 0}
              loading={loading}
              initialState={INITIAL_STATE}
              paginationModel={pagination}
              onPaginationModelChange={handlePaginationModelChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              pageSizeOptions={[5, 10, 20]}
              paginationMode="server"
              sortingMode="server"
            />
          )}
        </Box>
      </Box>
    </Box>
    </Card>
  );
};

export default Roles;
